import { FileText, RefreshCw, ShieldAlert } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { listAuditEvents, type AuditEventRow } from '../api';
import { StatusChip } from '../components/Avatar';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { useSession } from '../contexts/SessionContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatAbsoluteDate, formatRelativeDate } from '../utils/formatters';

// Friendly labels for well-known action patterns; falls back to the
// recorded description, then the raw pattern.
const ACTION_LABELS: Record<string, string> = {
  'cindro.http.PostLogin': 'Signed in',
  'cindro.http.PostLogout': 'Signed out',
  'cindro.http.PostSignup': 'Account created',
  'cindro.http.PostChangePassword': 'Password changed',
  'cindro.http.PostForgotPassword': 'Password reset requested',
  'cindro.http.PostResetPassword': 'Password reset completed',
  'cindro.http.PostOrganizations': 'Organization created',
  'cindro.http.PostOrganizationApps': 'Project created',
  'cindro.http.PostOrganizationAppEnvironments': 'Environment created',
  'cindro.http.PostOrganizationTeams': 'Team created',
  'cindro.http.PostApiKeys': 'API key created',
  'cindro.http.PostReleases': 'Bundle registered',
  'cindro.http.PostReleaseChannels': 'Release target created',
  'cindro.http.PatchReleaseChannel': 'Release target updated',
  'cindro.http.PostReleasePromote': 'Bundle deployed',
  'cindro.http.PostReleaseRollback': 'Release rolled back',
  'cindro.http.PostRuntimeBootstrapConfigs': 'Runtime config created',
  'cindro.http.PatchRuntimeBootstrapConfig': 'Runtime config updated',
  'cindro.http.DeleteRuntimeBootstrapConfig': 'Runtime config deleted',
  'cindro.http.PutReleaseStorageConfig': 'Storage config updated',
};

function actionLabel(event: AuditEventRow): string {
  return ACTION_LABELS[event.action] || event.description || event.action;
}

function toneForEvent(event: AuditEventRow): 'success' | 'warn' | 'neutral' {
  if (event.method === 'DELETE' || event.action.includes('Rollback')) return 'warn';
  if (event.method === 'POST') return 'success';
  return 'neutral';
}

function AuditLogPage() {
  const { session } = useSession();
  const { activeOrganization } = useWorkspace();

  const [events, setEvents] = useState<AuditEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!session || !activeOrganization) return;
    setLoading(true);
    setError('');
    try {
      setEvents(await listAuditEvents(session.accessToken, activeOrganization.id));
    } catch (error: unknown) {
      const response = typeof error === 'object' && error !== null && 'response' in error
        ? (error as { response?: { status?: number; data?: { message?: string } } }).response
        : undefined;
      const status = response?.status;
      setError(
        status === 403
          ? 'Only organisation owners and admins can view the audit log.'
          : response?.data?.message || (error instanceof Error ? error.message : 'Failed to load audit events.')
      );
    } finally {
      setLoading(false);
    }
  }, [session, activeOrganization]);

  useEffect(() => { void load(); }, [load]);

  return (
    <SectionCard
      label="Audit"
      title="Audit log"
      description="Every mutating action in this organisation, recorded at the API gateway."
      action={
        <button type="button" className="panelControl" onClick={() => void load()} disabled={loading}>
          <RefreshCw size={14} />
          <span>{loading ? 'Loading…' : 'Refresh'}</span>
        </button>
      }
    >
      {error ? (
        <EmptyState
          icon={<ShieldAlert size={20} />}
          title="Could not load audit log"
          description={error}
        />
      ) : loading && events.length === 0 ? (
        <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>
          Loading audit events…
        </div>
      ) : events.length === 0 ? (
        <EmptyState
          icon={<FileText size={20} />}
          title="No recorded events"
          description="New mutations will appear after audit capture is deployed. Earlier workspace history is not backfilled."
        />
      ) : (
        <div className="activityList">
          {events.map((event) => (
            <div key={event.auditEventId} className="activityRow">
              <span className={`activityIcon tone-${toneForEvent(event)}`}>
                <FileText size={16} />
              </span>
              <div className="activityCopy">
                <strong>{actionLabel(event)}</strong>
                <span>
                  {event.email || 'Unknown actor'} · {event.method} {event.path}
                </span>
              </div>
              <span
                className="deploymentTime"
                title={formatAbsoluteDate(event.createdAt)}
                style={{ marginRight: 10 }}
              >
                {formatRelativeDate(event.createdAt)}
              </span>
              <StatusChip tone={toneForEvent(event)}>
                {event.status}
              </StatusChip>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

export { AuditLogPage };
